import type { TFunction } from 'i18next';
import { z } from 'zod';

import type { GetSerieDetailResponse } from '@/api/generated/schemas';
import { toSerieTransportKey, type SerieTransportKey } from '../internal/serieLayers';

/** Možnosti přepínače typu dopravy (sdílené se zakládacím formulářem série). */
export const SERIE_TRANSPORT_OPTIONS = [
  'flight',
  'bus',
  'combined',
] as const satisfies readonly SerieTransportKey[];

/** Nabídka výchozí měny pro zadávání nákladů; prázdná hodnota = domácí měna. */
export const SERIE_COST_CURRENCY_OPTIONS = ['', 'CZK', 'EUR', 'PLN', 'USD'] as const;

/**
 * Factory pro Zod schéma editace termínového seriálu. Lokalizované hlášky se vkládají přímo do schématu
 * (single source of truth), proto se staví z `t` až za běhu. Kapacita a délka jsou drženy jako string
 * (přirozené pro číselný input) a validují se na kladné celé číslo.
 */
export function buildSerieDetailFormSchema(t: TFunction) {
  const positiveIntString = (message: string) =>
    z
      .string()
      .refine((value) => /^\d+$/.test(value.trim()) && Number(value.trim()) > 0, { message });

  return z.object({
    name: z.string().trim().min(1, t('serieDetail.validation.nameRequired')),
    transportType: z.enum(SERIE_TRANSPORT_OPTIONS),
    capacityPerTerm: positiveIntString(t('serieDetail.validation.capacityPositive')),
    mainProgramLengthDays: positiveIntString(t('serieDetail.validation.lengthPositive')),
    note: z.string(),
    defaultCostCurrency: z.enum(SERIE_COST_CURRENCY_OPTIONS),
  });
}

export type SerieDetailFormValues = z.infer<ReturnType<typeof buildSerieDetailFormSchema>>;

/** Mapuje načtený detail seriálu na výchozí hodnoty formuláře. */
export function fromGetSerieDetailResponse(detail: GetSerieDetailResponse): SerieDetailFormValues {
  return {
    name: detail.name,
    transportType: toSerieTransportKey(detail.transportType),
    capacityPerTerm: String(detail.capacityPerTerm),
    mainProgramLengthDays: String(detail.mainProgramLengthDays),
    note: detail.note ?? '',
    defaultCostCurrency: normalizeCostCurrency(detail.defaultCostCurrency),
  };
}

/** Zúží libovolnou serverovou hodnotu měny na jednu z {@link SERIE_COST_CURRENCY_OPTIONS} (jinak prázdná = domácí). */
function normalizeCostCurrency(
  value: string | null | undefined
): (typeof SERIE_COST_CURRENCY_OPTIONS)[number] {
  const found = SERIE_COST_CURRENCY_OPTIONS.find((option) => option === value);
  return found ?? '';
}
